"use client";
/**
 * The work of a spoken turn, on the ball (spec 2026-09-12 §6). Nothing floats
 * above the ball in a chat bubble: the receipt for a task the line made
 * surfaces from the ball's crown as a 300 px paper joined to the glass by a
 * lit neck, stays five seconds, and sinks back. Trouble lands beneath it
 * (or alone on the paper), eight seconds. A spoken answer is heard, not
 * read: only the receipt and trouble are ever words here. The turn's events
 * fold through the SSE lane's own reducer (lineWorkModel.ts), so a spoken
 * ask and a typed ask can never disagree about the truth; the receipt is held
 * across the reset the linger makes, keyed on the delegation id the socket
 * handler stamps, so a late failure of the same delegation still shows its
 * task. What sinks is an afterimage: the door and the trouble leave with the
 * view, and the paper that goes back into the crown is nothing to click and
 * nothing for a reader to be told twice. The paper's rules live in
 * globals.css (the cl- stage section) and its live region is the stage's,
 * mounted before any paper; the crown carries neither.
 */
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ComposerPanel, RouterEvent } from "@bash/shared";
import { cn } from "@/lib/utils";
import { contextOf } from "@/lib/composer/screen-context";
import { shortLabelForModel } from "@/components/ui/model-catalog";
import { useComposerDotsStore } from "@/stores/composerDotsStore";
import { useTaskStore } from "@/stores/taskStore";
import { DRAWN_KINDS } from "@/components/composer/panels/drawn";
import { PanelCard } from "@/components/composer/panels/PanelCard";
import { PanelDeck } from "@/components/composer/panels/PanelDeck";
import { undoRunApi } from "@/lib/composer/undo-client";
import { STATUS_WORD } from "@/lib/task-live-status";
import type { LineSnapshot } from "./lineMachine";
import {
  deriveWork,
  foldTurn,
  receiptKey,
  type WorkReceipt,
  type WorkView,
} from "./lineWorkModel";
import { useLiveStore } from "./liveStore";
import { isSettled, useTaskLiveStatus } from "./useTaskLiveStatus";
import { useDockRouter } from "./useDockRouter";
import { forwardAction, forwardRoute } from "@/lib/dock-window";

/** The receipt lingers this long, then sinks (the shipped receipt linger). */
export const RECEIPT_LINGER_MS = 5000;
/** Trouble and an empty wallet stay long enough to be read twice. */
export const TROUBLE_LINGER_MS = 8000;
/** The paper's transform back into the crown; it stays mounted this long after the view lets go. */
export const SINK_MS = 500;
/** A sheet needs more than the receipt's 300 px (ruling e): set inline only while one is up; the stylesheet's `--cl-cw: 300px` stands for every other paper. */
export const WIDE_PANEL_PX = 400;
const WIDE_KINDS: ReadonlySet<ComposerPanel["kind"]> = new Set([
  "sheet",
  "connect",
  "confirm",
]);
/** The crown's × and the deck's Escape both do exactly this; module-level, so
 *  the deck's key listener is registered once and never re-ordered. */
const closePanel = () => useLiveStore.getState().setPanel(null);

/**
 * The task the person is looking at, or null. The app's one rule for that
 * question (screen-context, the same function the screen beacon and the toast
 * drop ask): this route is a task's page AND the store's pick is that task, so
 * a selectedTaskId that outlived the page it was picked on answers nothing.
 */
function useViewingTaskId(): string | null {
  const pathname = usePathname();
  const selectedTaskId = useTaskStore((s) => s.selectedTaskId ?? null);
  return contextOf(pathname ?? "", selectedTaskId).activeTaskId ?? null;
}

/**
 * The work's view for the stage. The last task receipt is held across the
 * reset the linger makes (the dismiss empties the turn but keeps the
 * delegation id): a failure of the same delegation lands later as a lone
 * error and must still show its task. Keyed on the task and the delegation
 * id the handler stamps, never on event shape or a fold's identity (a memo
 * React may drop), and let go the moment the id changes. It is information
 * from previous renders, kept the way React keeps it: state set during
 * render, which re-runs the hook at once with the receipt in place.
 */
export function useWork(
  turn: RouterEvent[],
  line: LineSnapshot,
  check: boolean
): WorkView {
  const delegationId = useLiveStore((s) => s.turnDelegationId);
  const panel = useLiveStore((s) => s.panel);
  // The crown says nothing about the task the person is standing in: their
  // words already landed as a bubble in that column (the founder, 2026-09-21).
  const viewingTaskId = useViewingTaskId();
  const fold = useMemo(() => foldTurn(turn), [turn]);
  const [held, setHeld] = useState<{
    id: string | null;
    receipt: WorkReceipt;
  } | null>(null);
  if (fold.receipt) {
    const same =
      held !== null &&
      receiptKey(held.receipt) === receiptKey(fold.receipt) &&
      held.id === delegationId;
    if (!same) setHeld({ id: delegationId, receipt: fold.receipt });
  } else if (held && held.id !== delegationId) {
    setHeld(null);
  }
  const heldReceipt = held?.receipt ?? null;
  // The check yields to trouble (ruling): a late error that resurfaces the
  // paper inside the check window must not leave a done mark in the glass
  // under "did not go through". deriveWork does not gate it; the consumer does.
  const troubled =
    !fold.superseded &&
    (fold.ui.phase === "error" || fold.ui.phase === "credits");
  const mark = check && !troubled;
  return useMemo(
    () =>
      deriveWork({
        fold,
        held: heldReceipt,
        line,
        check: mark,
        panel,
        viewingTaskId,
      }),
    [fold, heldReceipt, line, mark, panel, viewingTaskId]
  );
}

export interface LineWorkProps {
  view: WorkView;
  /** Called when the paper leaves: its linger ended, Escape, or the row was
   *  clicked. `clean` is a receipt with no trouble beneath. */
  onDismiss: (result: { clean: boolean }) => void;
}

export function LineWork({ view, onDismiss }: LineWorkProps) {
  // In the shell's Dock window a push goes to the main window (useDockRouter).
  const router = useDockRouter();
  const { up } = view;
  // The sink: the paper stays mounted half a second after the view lets go,
  // so it can sink back into the crown; what sinks is the last view up.
  // Both are information from previous renders, kept as React keeps it:
  // state set during render, re-run at once.
  const [lastUp, setLastUp] = useState(view);
  if (up && lastUp !== view) setLastUp(view);
  const shown = up ? view : lastUp;
  const [mounted, setMounted] = useState(up);
  if (up && !mounted) setMounted(true);
  // One sink, single-flight and state-driven: the let-go (up to false)
  // starts the one half-second timer from that moment, and the effect's
  // cleanup is its cancel, so the view coming back, a newer let-go, or an
  // unmount each clear it. A dismiss renders the stage's let-go
  // synchronously (flushSync below), so the sink starts on the clock the
  // linger promised; a dismiss the stage does not follow is no transition
  // and arms nothing.
  const wasUp = useRef(up);
  useEffect(() => {
    const letGo = wasUp.current && !up;
    wasUp.current = up;
    if (!letGo) return;
    const t = setTimeout(() => setMounted(false), SINK_MS);
    return () => clearTimeout(t);
  }, [up]);

  const receipt = shown.receipt;
  const hasTrouble = shown.trouble !== null;
  const clean = receipt !== null && !hasTrouble;
  const taskReceipt =
    receipt &&
    (receipt.kind === "task_created" || receipt.kind === "task_followup")
      ? receipt
      : null;
  // The Line moved you (spec 2026-09-17 §5.4): the place, for the row alone.
  const appReceipt = receipt && receipt.kind === "app_control" ? receipt : null;
  // The Line handed back a link (R-L11): the row is the link itself.
  const linkReceipt = receipt && receipt.kind === "open_url" ? receipt : null;
  // The crown's one panel (composer panels, spec 2026-09-19 §3.4): no timer,
  // its own close. Two panels, and they are not the same one (R-13):
  // `shown.panel` is what is DRAWN, so the afterimage keeps its card while the
  // paper sinks; `view.panel` is what is LIVE, and it decides the two things
  // that must go the moment the panel does — the Escape claim and the width.
  // Keep both; collapsing them widens a crown that is already on its way down.
  const panel = shown.panel;
  // R-15: drawn AND wide, never wide alone. A connect panel is both (it draws
  // the pause card and WIDE_KINDS lists it); a `confirm`, still drawn by
  // nobody, leaves the crown down and must not set the width on a crown that
  // is down.
  const drawn =
    view.panel !== null && DRAWN_KINDS.has(view.panel.kind) ? view.panel : null;
  const wide = drawn !== null && WIDE_KINDS.has(drawn.kind);
  /** A drawn panel owns Escape while it is up (R-22): the crown stands down. */
  const panelOwnsEscape = drawn !== null;
  // Only a receipt or trouble is owed a timer: a panel stays until it is
  // closed (spec §3.3).
  const paperOwed = receipt !== null || hasTrouble;

  const fileDot = () => {
    if (!taskReceipt) return;
    useComposerDotsStore.getState().add({
      taskId: taskReceipt.taskId,
      title: taskReceipt.title,
      model: taskReceipt.model ?? undefined,
    });
  };
  // Rendered synchronously: the stage's let-go (its resetTurn) lands on this
  // beat, so the sink starts now and not on React's next turn.
  const dismiss = () => {
    flushSync(() => onDismiss({ clean }));
  };
  // The linger's end and Escape fire from effects; an Effect Event runs with
  // the render that is current when it fires, so neither needs a ref to
  // the latest props, and neither restarts its effect on every render.
  const onLingerEnd = useEffectEvent(() => {
    fileDot();
    dismiss();
  });
  const onEscape = useEffectEvent(() => dismiss());

  // The linger: five seconds for a clean receipt, eight with trouble or an
  // empty wallet. Keyed on the receipt (the task, or the place) and on whether
  // trouble is there, so a re-render never restarts the beat and trouble
  // landing beneath a receipt does (it must be read, not folded away with
  // the receipt).
  const key = receipt ? receiptKey(receipt) : null;
  useEffect(() => {
    // panels have no timer (spec §3.3): only a receipt or trouble lingers
    if (!up || !paperOwed) return;
    const t = setTimeout(
      () => onLingerEnd(),
      hasTrouble ? TROUBLE_LINGER_MS : RECEIPT_LINGER_MS
    );
    return () => clearTimeout(t);
  }, [up, key, hasTrouble, paperOwed]);

  // Escape dismisses the paper and leaves the line open. The paper CLAIMS
  // the key (preventDefault, window capture) so the Line's own Escape (the
  // Dock's, bubble phase, yielding to defaultPrevented) never hangs up on
  // the same press; and it yields to a press already claimed (the inline
  // card's paper, a focused field), so one Escape never closes two things.
  //
  // While a drawn panel is up the crown does not listen AT ALL (R-22): the
  // deck claims Escape for the panel in the same capture phase, and two
  // capture listeners fire in registration order, so yielding to a claimed
  // press would not have decided it (the crown's listener is the older one).
  // One press takes the panel down; the next finds the crown listening again
  // and sinks the paper exactly as it does today.
  useEffect(() => {
    if (!up || panelOwnsEscape) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      onEscape();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [up, panelOwnsEscape]);

  // The status rides the shared truth ladder (live phase, story spine,
  // lifecycle), the same truth the board, the dots and the Dock's settle read.
  const status = useTaskLiveStatus(taskReceipt?.taskId ?? null);
  const model = taskReceipt?.model
    ? shortLabelForModel(taskReceipt.model)
    : null;
  // A substituted model is never silent (founder 08-13): the paper says the
  // swap in the person's own vocabulary, as the inline card's receipt does.
  const swap =
    taskReceipt?.kind === "task_created" && taskReceipt.substitutedFrom && model
      ? { from: shortLabelForModel(taskReceipt.substitutedFrom), to: model }
      : null;

  if (!up && !mounted) return null;
  const rowBody = taskReceipt ? (
    <>
      <span className="cl-crown__t">
        <span className="cl-crown__say">
          {taskReceipt.say ?? taskReceipt.title}
        </span>
        <span className="cl-crown__meta">
          <i
            className={cn(
              "cl-crown__dot",
              isSettled(status) && "cl-crown__dot--still"
            )}
            data-status={status}
            aria-hidden
          />
          {STATUS_WORD[status]}
          {model && (
            <>
              <b>·</b>
              {model}
            </>
          )}
          <b>·</b>
          just now
        </span>
        {swap && (
          <span className="cl-crown__swap">
            {swap.from} can&apos;t see the screen, so this one runs on {swap.to}
          </span>
        )}
      </span>
      <span className="cl-crown__chev" aria-hidden>
        ›
      </span>
    </>
  ) : appReceipt ? (
    // The Line moved you (spec 2026-09-17 §5.4): one line, the place, the tap goes there.
    <>
      <span className="cl-crown__t">
        <span className="cl-crown__say">Opened {appReceipt.label}</span>
        <span className="cl-crown__meta">just now</span>
      </span>
      <span className="cl-crown__chev" aria-hidden>
        ›
      </span>
    </>
  ) : linkReceipt ? (
    // R-L11: the link the Line said is on the screen, whether or not the
    // automatic open went through.
    <>
      <span className="cl-crown__t">
        <span className="cl-crown__say">Open {linkReceipt.label}</span>
        <span className="cl-crown__meta">just now</span>
      </span>
      <span className="cl-crown__chev" aria-hidden>
        ›
      </span>
    </>
  ) : null;
  return (
    <div
      className="cl-crown"
      data-up={up ? "1" : "0"}
      data-wide={wide ? "1" : "0"}
      data-testid="line-work"
      style={wide ? { ["--cl-cw" as string]: `${WIDE_PANEL_PX}px` } : undefined}
    >
      {up ? (
        <div className="cl-crown__paper">
          {panel && (
            // The deck, not a bare card: it owns the panel's Escape claim
            // (R-22), and one panel is the list the crown ever holds.
            <div data-testid="line-work-panel">
              <PanelDeck
                panels={[panel]}
                onClose={closePanel}
                onOpen={(route) => {
                  router.push(route);
                  closePanel();
                }}
                onUndo={undoRunApi}
              />
            </div>
          )}
          {receipt &&
            (receipt.kind === "open_url" ? (
              // R-L11: a real anchor, so the tap is a gesture no browser
              // blocks; the url passed the belt's gate at the fold.
              <a
                className="cl-crown__row"
                data-testid="line-work-receipt"
                href={receipt.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${receipt.label}`}
                onClick={(e) => {
                  // The shell's Dock window: the main window opens it (the system browser).
                  if (
                    forwardAction({
                      kind: "open_url",
                      url: receipt.url,
                      label: receipt.label,
                    })
                  )
                    e.preventDefault();
                  dismiss();
                }}
              >
                {rowBody}
              </a>
            ) : (
              <button
                type="button"
                className="cl-crown__row"
                data-testid="line-work-receipt"
                aria-label={
                  receipt.kind === "app_control"
                    ? `Open ${receipt.label}`
                    : `Open task: ${receipt.title}`
                }
                onClick={() => {
                  if (receipt.kind === "app_control") {
                    router.push(receipt.route);
                  } else {
                    fileDot();
                    router.push(`/task/${receipt.taskId}`);
                  }
                  dismiss();
                }}
              >
                {rowBody}
              </button>
            ))}
          {shown.trouble && (
            <div
              className={cn(
                "cl-crown__trouble",
                !receipt && "cl-crown__trouble--only"
              )}
              data-testid="line-work-trouble"
            >
              {shown.trouble}
              {shown.credits && (
                <>
                  {" "}
                  <Link
                    href="/account/plan"
                    className="cl-crown__link"
                    // The shell's Dock window: the plan opens in the main window, never the panel.
                    onClick={(e) => {
                      if (forwardRoute("/account/plan")) e.preventDefault();
                    }}
                  >
                    Top up
                  </Link>
                </>
              )}
            </div>
          )}
        </div>
      ) : (
        // The afterimage that sinks: the same paper with no door and no
        // live region content, so nothing is clickable or announced twice.
        <div className="cl-crown__paper" aria-hidden>
          {panel && (
            // The card alone, never the deck: an afterimage claims no key and
            // nothing in it is clickable.
            <div className="ts-root pointer-events-none">
              <PanelCard
                panel={panel}
                onClose={() => {}}
                onOpen={() => {}}
                onUndo={undoRunApi}
              />
            </div>
          )}
          {receipt && <span className="cl-crown__row">{rowBody}</span>}
          {shown.trouble && (
            <div
              className={cn(
                "cl-crown__trouble",
                !receipt && "cl-crown__trouble--only"
              )}
            >
              {shown.trouble}
              {shown.credits && (
                <>
                  {" "}
                  <span className="cl-crown__link">Top up</span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
